import { createFileRoute, Link } from "@tanstack/react-router";

import { Faq } from "@/components/Faq";
import { SalonRatingLines } from "@/components/SalonRatingLines";
import { SiteShell } from "@/components/layout/SiteShell";
import { Button } from "@/components/ui/button";
import { PRICE, priceRows } from "@/data/pricing";
import { reviewsForSalon } from "@/data/reviews";
import { salons } from "@/data/salons";
import { withBookingUtm } from "@/lib/booking";
import { internalPath } from "@/lib/paths";
import {
  SITE_URL,
  absUrl,
  breadcrumbSchema,
  faqSchema,
  jsonLd,
  localBusinessSchema,
} from "@/lib/seo";

const TITLE = `Barberare Stockholm: 4 salonger, herrklippning ${PRICE.herrklippning} kr | Tailor's Barber`;
const DESCRIPTION = `Barberare i Stockholm: Tailor's Barber har fyra salonger (City vid T-Centralen, Täby Centrum, Arninge, Åkersberga). Herrklippning ${PRICE.herrklippning} kr, skin fade, skägg. Öppet alla dagar. Boka online.`;
const CANONICAL = `${SITE_URL}/barberare-stockholm/`;

const salonOrder = ["ahlens-city", "taby", "arninge", "akersberga"] as const;
const hubSalons = salonOrder.map((slug) => {
  const salon = salons.find((item) => item.slug === slug);
  if (!salon) throw new Error(`Salong saknas: ${slug}`);
  return salon;
});

const locationNotes: Record<(typeof salonOrder)[number], string> = {
  "ahlens-city": "Åhléns City för dig som jobbar eller pendlar i city.",
  taby: "Täby Centrum, vår flaggskeppssalong, för norra Stockholm.",
  arninge: "Arninge för Täby, Vallentuna och Norrtälje-vägen.",
  akersberga: "Åkersberga för Österåker och Roslagen.",
};

const priceTableRows = priceRows([
  "Herrklippning",
  "Skin Fade / Taper Fade",
  "Skäggtrimning",
  "Klippning & Skäggtrimning",
  "Barnklippning",
  "Seniorklippning 65+",
]).map(([name, price]) => [name.replaceAll("–", " till "), price]);

const comparisonRows = [
  ["Pris herrklippning", `${PRICE.herrklippning} kr`, "400 till 800 kr"],
  ["Antal salonger", "4", "1"],
  ["Öppet söndag", "Ja, alla salonger", "Varierar"],
  ["Drop-in", "Ja, i mån av tid", "Varierar"],
  ["Onlinebokning", "BokaDirekt, välj barberare", "Varierar"],
  ["Publika omdömen", "Google och BokaDirekt, visas var för sig", "Varierar"],
];

const reasons = [
  {
    title: "Specialister på fade",
    text: "Skin fade, taper fade, low, mid och high. Övergången byggs i flera steg med maskin och rakkniv tills skarven inte syns.",
  },
  {
    title: "Skägg och rakning",
    text: "Formning efter käklinje, linjering med rakkniv, varm handduk och skäggolja.",
  },
  {
    title: "Samma standard på fyra platser",
    text: "Bokar du i City eller i Åkersberga får du samma priser, samma tjänster och samma utbildning bakom saxen.",
  },
  {
    title: "Boka den barberare du vill",
    text: "Alla barberare har egna profiler och omdömen på BokaDirekt. Välj namn eller nästa lediga tid.",
  },
];

const services = [
  { name: "Herrklippning", path: "/herrklippning/", price: PRICE.herrklippning },
  { name: "Skin fade", path: "/skin-fade/", price: PRICE.fade },
  { name: "Taper fade", path: "/taper-fade/", price: PRICE.fade },
  { name: "Skäggtrimning", path: "/skaggtrimning/", price: PRICE.skaggtrimning },
  {
    name: "Klippning & skägg",
    path: "/herrklippning-skaggtrimning/",
    price: PRICE.klippningSkagg,
  },
  {
    name: "Barnklippning",
    path: "/barnklippning-stockholm/",
    price: PRICE.barnklippning,
  },
];

const faq = [
  {
    q: "Vilken är den bästa barberaren i Stockholm?",
    a: "Det beror på vad du vill ha. Är det fade, skägg och konsekvent resultat är Tailor's Barber ett av de mest bokade valen i Stockholm, med fyra salonger och tusentals publika omdömen på Google och BokaDirekt. Läs vår genomgång av vad som skiljer en bra barberare från en medelmåttig.",
    answer: (
      <>
        Det beror på vad du vill ha. Är det fade, skägg och konsekvent resultat är Tailor&apos;s
        Barber ett av de mest bokade valen i Stockholm, med fyra salonger och tusentals publika
        omdömen på Google och BokaDirekt.{" "}
        <Link to={internalPath("/basta-barberare-i-stockholm-2026")} className="underline">
          Läs vår genomgång av vad som skiljer en bra barberare från en medelmåttig.
        </Link>
      </>
    ),
  },
  {
    q: "Vad kostar en barberare i Stockholm?",
    a: `Herrklippning kostar ${PRICE.herrklippning} kr hos Tailor's Barber, skäggtrimning ${PRICE.skaggtrimning} kr och hår och skägg ${PRICE.klippningSkagg} kr. Snittet i Stockholm ligger på 400 till 800 kr för en herrklippning.`,
  },
  {
    q: "Vilken barberare ligger närmast T-Centralen?",
    a: "Tailor's Barber Åhléns City på Klarabergsgatan 50 ligger 60 sekunder från T-Centralen, inne i Åhléns.",
  },
  {
    q: "Har ni öppet på söndagar?",
    a: "Ja, alla fyra salonger har öppet på söndagar. Åhléns City 11:00 till 19:00, övriga 10:00 till 18:00.",
    answer: (
      <Link to={internalPath("/barberare-oppet-sondag-stockholm")} className="underline">
        Ja, alla fyra salonger har öppet på söndagar. Åhléns City 11:00 till 19:00, övriga 10:00
        till 18:00.{" "}
      </Link>
    ),
  },
  {
    q: "Kan jag komma på drop-in?",
    a: "Ja, drop-in tas emot i mån av tid på alla salonger. Vill du vara säker på en tid, boka online via BokaDirekt.",
  },
  {
    q: "Hur bokar jag en specifik barberare?",
    a: "Välj salong på BokaDirekt, sedan barberare och tid. Varje barberare har en egen profil med omdömen.",
  },
  {
    q: "Är Tailor's Barber en barberare eller frisör?",
    a: "Vi är en barbershop: vi klipper herr, barn och seniorer och arbetar med fade, skägg och rakning. Vi gör inte färg eller längre damfrisyrer.",
  },
];

function compactHours(hours: string): string {
  return hours.replaceAll(":00", "").replace(/[\u2013-]/g, " till ");
}

function hoursSummary(salon: (typeof hubSalons)[number]): string {
  const weekday = compactHours(salon.openingHours[0]?.hours ?? "");
  const saturday = compactHours(salon.openingHours[5]?.hours ?? "");
  const sunday = compactHours(salon.openingHours[6]?.hours ?? "");
  return `Mån till fre ${weekday}, lör ${saturday}, sön ${sunday}`;
}

export const Route = createFileRoute("/barberare-stockholm")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: absUrl(CANONICAL) },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: absUrl(CANONICAL) }],
    scripts: [
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Barberare Stockholm", url: "/barberare-stockholm/" },
        ]),
      ),
      jsonLd(faqSchema(faq)),
      jsonLd({
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Tailor's Barber salonger i Stockholm",
        itemListElement: hubSalons.map((salon, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: localBusinessSchema(salon, reviewsForSalon(salon.slug)),
        })),
      }),
    ],
  }),
  component: BarberareStockholmPage,
});

function BarberareStockholmPage() {
  const citySalon = hubSalons[0];

  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-8">
        <nav className="text-xs text-muted-foreground mb-6" aria-label="Brödsmulor">
          <Link to="/" className="hover:underline">
            Hem
          </Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Barberare Stockholm</span>
        </nav>
        <div className="eyebrow">Barberare i Stockholm</div>
        <h1 className="mt-3 max-w-5xl text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Barberare i Stockholm: fyra salonger, samma hantverk
        </h1>
        <p className="mt-5 max-w-4xl text-lg text-foreground/85 leading-relaxed font-medium">
          Tailor&apos;s Barber är en barberarkedja i Stockholm med fyra salonger: Åhléns City 60
          sekunder från T-Centralen, Westfield Täby Centrum, ICA Maxi Arninge och ICA Maxi
          Åkersberga. Herrklippning kostar {PRICE.herrklippning} kr, skäggtrimning{" "}
          {PRICE.skaggtrimning} kr och hår och skägg {PRICE.klippningSkagg} kr på alla salonger.
          Öppet sju dagar i veckan, drop-in i mån av tid.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <a
              href={withBookingUtm(citySalon.bokaDirektUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Boka tid online
            </a>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to={internalPath("/priser")}>Se alla priser</Link>
          </Button>
        </div>
      </section>

      <section className="container-page py-10 md:py-16">
        <h2 className="text-3xl md:text-4xl">Välj salong</h2>
        <div className="mt-7 grid gap-6 md:grid-cols-2">
          {hubSalons.map((salon) => (
            <article key={salon.slug} className="card-elev overflow-hidden flex flex-col">
              <img
                src={salon.imageThumb ?? salon.image}
                alt={`${salon.name}, barberare i Stockholm`}
                width={480}
                height={320}
                loading="lazy"
                className="aspect-[3/2] w-full object-cover"
              />
              <div className="p-6 flex flex-1 flex-col">
                <h3 className="text-2xl">{salon.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {salon.address}, {salon.postal} {salon.city}
                </p>
                <SalonRatingLines salon={salon.slug} compact className="mt-3 text-sm" />
                <p className="mt-3 text-sm text-foreground/75">{hoursSummary(salon)}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button asChild variant="outline">
                    <Link to={internalPath(salon.url)}>Om salongen</Link>
                  </Button>
                  <Button asChild>
                    <a
                      href={withBookingUtm(salon.bokaDirektUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Boka
                    </a>
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="mt-7 max-w-4xl text-foreground/80 leading-relaxed">
          {salonOrder.map((slug) => locationNotes[slug]).join(" ")}
        </p>
      </section>

      <section className="container-page py-10 md:py-16 border-t border-border">
        <h2 className="text-3xl md:text-4xl">Priser hos Tailor&apos;s Barber i Stockholm</h2>
        <div className="mt-7 card-elev overflow-x-auto max-w-3xl">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border/60">
                <th className="px-5 py-4 font-medium">Tjänst</th>
                <th className="px-5 py-4 text-right font-medium">Pris</th>
              </tr>
            </thead>
            <tbody>
              {priceTableRows.map(([name, price], index) => (
                <tr key={name} className={index > 0 ? "border-t border-border/60" : ""}>
                  <td className="px-5 py-4">{name}</td>
                  <td className="px-5 py-4 text-right whitespace-nowrap font-medium">{price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 max-w-3xl text-foreground/80 leading-relaxed">
          Samma pris på alla fyra salonger och alla veckodagar. En herrklippning i Stockholm kostar
          vanligtvis 400 till 800 kr; vi ligger på {PRICE.herrklippning} kr med konsultation,
          klippning, nackrakning, tvätt och styling inkluderat.
        </p>
      </section>

      <section className="container-page py-10 md:py-16">
        <h2 className="text-3xl md:text-4xl">Så jämför du barberare i Stockholm</h2>
        <div className="mt-7 card-elev overflow-x-auto">
          <table className="w-full min-w-[680px] text-left">
            <thead>
              <tr className="border-b border-border/60">
                <th className="px-5 py-4 font-medium">Att jämföra</th>
                <th className="px-5 py-4 font-medium">Tailor&apos;s Barber</th>
                <th className="px-5 py-4 font-medium">Vanligt i Stockholm</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map(([label, ours, common], index) => (
                <tr key={label} className={index > 0 ? "border-t border-border/60" : ""}>
                  <td className="px-5 py-4 font-medium">{label}</td>
                  <td className="px-5 py-4">{ours}</td>
                  <td className="px-5 py-4">{common}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="container-page py-10 md:py-16 border-t border-border">
        <h2 className="text-3xl md:text-4xl">Varför Tailor&apos;s Barber</h2>
        <div className="mt-7 grid gap-5 md:grid-cols-2">
          {reasons.map((reason) => (
            <article key={reason.title} className="card-elev p-6">
              <h3 className="text-xl">{reason.title}</h3>
              <p className="mt-2 text-foreground/80 leading-relaxed">{reason.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="container-page py-10 md:py-16">
        <h2 className="text-3xl md:text-4xl">Barberare nära dig i Stockholm</h2>
        <div className="mt-6 max-w-4xl space-y-4 text-foreground/80 leading-relaxed">
          <p>
            För Norrmalm och City ligger{" "}
            <Link to={internalPath("/barberare-stockholm-ahlens-city")} className="underline">
              Åhléns City
            </Link>{" "}
            vid T-Centralen. Läs också om vår{" "}
            <Link to={internalPath("/barberare-norrmalm")} className="underline">
              barberare på Norrmalm
            </Link>
            .
          </p>
          <p>
            Från Vasastan och Kungsholmen tar du dig till{" "}
            <Link to={internalPath("/barberare-stockholm-ahlens-city")} className="underline">
              Åhléns City
            </Link>{" "}
            på cirka 5 till 10 minuter med tunnelbana eller buss.
          </p>
          <p>
            Från Östermalm ligger{" "}
            <Link to={internalPath("/barberare-stockholm-ahlens-city")} className="underline">
              Åhléns City
            </Link>{" "}
            cirka 5 minuter med tunnelbana från Östermalmstorg.
          </p>
          <p>
            Från Södermalm och Gamla stan når du{" "}
            <Link to={internalPath("/barberare-stockholm-ahlens-city")} className="underline">
              Åhléns City
            </Link>{" "}
            på cirka 5 minuter med tunnelbana från Slussen.
          </p>
          <p>
            Från Solna och Sundbyberg är{" "}
            <Link to={internalPath("/barberare-stockholm-ahlens-city")} className="underline">
              Åhléns City
            </Link>{" "}
            nära via pendeltåg till Stockholm City.
          </p>
          <p>
            För Täby och Danderyd är{" "}
            <Link to={internalPath("/barberare-stockholm-taby")} className="underline">
              Täby Centrum
            </Link>{" "}
            närmast, med Roslagsbanan till Täby centrum.
          </p>
          <p>
            För Vallentuna och Arninge finns{" "}
            <Link to={internalPath("/barberare-stockholm-arninge")} className="underline">
              Arninge
            </Link>{" "}
            vid E18.
          </p>
          <p>
            För Åkersberga och Österåker finns{" "}
            <Link to={internalPath("/barberare-stockholm-akersberga")} className="underline">
              Åkersberga
            </Link>{" "}
            inne i ICA Maxi.
          </p>
        </div>
      </section>

      <section className="container-page py-10 md:py-16 border-t border-border">
        <h2 className="text-3xl md:text-4xl">Tjänster</h2>
        <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <Link
              key={service.name}
              to={internalPath(service.path)}
              className="card-elev p-5 hover:border-brass-deep transition-colors"
            >
              <h3 className="font-medium">{service.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{service.price} kr</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Läs vidare om barberare i Stockholm</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Jämför salonger, betyg och vad du faktiskt får för pengarna i vår genomgång{" "}
          <Link to="/basta-barberare-i-stockholm-2026/" className="underline font-medium">
            Bästa barberare i Stockholm 2026
          </Link>
          . Behöver du en tid på helgen hittar du alla tider hos{" "}
          <Link to="/barberare-oppet-sondag-stockholm/" className="underline font-medium">
            barberare med öppet på söndagar i Stockholm
          </Link>
          .
        </p>
      </section>

      <Faq items={faq} heading="Vanliga frågor om barberare i Stockholm" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Boka barberare i Stockholm</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg">
              <a
                href={withBookingUtm(citySalon.bokaDirektUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Boka tid online
              </a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/">Se våra salonger</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
